import React from 'react';
import Card, { CardTitle } from '../../components/Card';
import Styles from './dashboardStyle.module.scss';
import { useNavigate } from 'react-router-dom';
import DynamicDataTooltip from '../../components/DynamicDataTooltip/DynamicDataTooltip';

type Props = {
  children?: React.ReactNode;
  className?: string;
};

const HeadItem = ({ children }: Props) => <div className="flex items-center justify-center font-semibold">{children}</div>;

const GridItem = ({ children, className, onClick }: Props & { onClick?: () => void }) => (
  <div
    onClick={onClick}
    className={`flex items-center justify-center h-[50px] rounded-[5px] text-white text-sm font-medium cursor-pointer transition-all hover:scale-105 ${className}`}
  >
    {children}
  </div>
);

const defaultDurations = ['>90', '61-90', '31-60', '<30'];
const defaultCategories = ['Critical', 'High', 'Medium', 'Low'];
const opacityMap = ['opacity-[1]', 'opacity-[.80]', 'opacity-[.60]', 'opacity-[.40]'];
const colorMap: Record<string, string> = {
  Critical: 'bg-[#A50811]',
  High: 'bg-[#CF2929]',
  Medium: 'bg-[#7D3434]',
  Low: 'bg-[#828282]',
};

const normalizeRangeToken = (input: string | null | undefined): string => {
  if (!input) return '';

  const compact = input.replace(/%3E/gi, '>').replace(/%3C/gi, '<').replace(/\s+/g, '').toLowerCase().replace(/days?/g, '');

  const gtMatch = compact.match(/^>(\d+)$/);
  if (gtMatch) return `>${gtMatch[1]}`;

  const ltMatch = compact.match(/^<(\d+)$/);
  if (ltMatch) return `<${ltMatch[1]}`;

  const rangeMatch = compact.match(/^(\d+)-(\d+)$/);
  if (rangeMatch) return `${rangeMatch[1]}-${rangeMatch[2]}`;

  return compact;
};

const formatRangeLabel = (range: string): string => {
  const token = normalizeRangeToken(range);
  const gtMatch = token.match(/^>(\d+)$/);
  if (gtMatch) return `> ${gtMatch[1]} days`;

  const ltMatch = token.match(/^<(\d+)$/);
  if (ltMatch) return `< ${ltMatch[1]} days`;

  const rangeMatch = token.match(/^(\d+)-(\d+)$/);
  if (rangeMatch) return `${rangeMatch[1]} - ${rangeMatch[2]} days`;

  return range;
};

type AdditionalInfoEntry = {
  packageIdentifier?: string;
  packageName?: string;
  currentVersion?: string;
  latestVersion?: string;
  patchType?: string;
  osName?: string;
};

type PatchBySeverityItem = {
  category: string;
  duration: string;
  numberOfApplications?: number;
  urlFilter?: string;
  additionalInfo?: Record<string, AdditionalInfoEntry> | null;
};

type PatchBySeverityProps = {
  patchBySeverity: PatchBySeverityItem[];
  groupId: string | null;
  severityOrder?: string[];
  durationOrder?: string[];
};

const getDisplayCount = (item: PatchBySeverityItem | undefined): number => {
  if (!item) return 0;

  const additionalInfo = item.additionalInfo;
  if (additionalInfo && typeof additionalInfo === 'object') {
    const actualRecordCount = Object.keys(additionalInfo).length;
    if (actualRecordCount > 0) {
      return actualRecordCount;
    }
  }

  return item.numberOfApplications ?? 0;
};

// additionalInfo keys look like "Mozilla.Firefox|Mozilla Firefox" for 3rd-party apps, or
// "osupdate|KB5094126|Windows 11, version 25H2" for OS updates - patchType is the reliable
// signal, the "osupdate|" key prefix is a fallback for entries missing it.
const isOsUpdateEntry = (key: string, entry: AdditionalInfoEntry): boolean =>
  entry?.patchType?.toLowerCase() === 'osupdate' || key.toLowerCase().startsWith('osupdate|');

// Builds the aggregate category-count map DynamicDataTooltip expects,
// e.g. "3rd Party Apps: 10" / "OS Update: 3".
// Note: the data-object keys must avoid the substring "date" (so e.g. not "osUpdates") -
// DynamicDataTooltip's formatFieldValue() treats any field name containing "date" as a
// date field and runs it through moment(), which mangles a plain count into a date string.
const buildAdditionalInfoTooltip = (additionalInfo: Record<string, AdditionalInfoEntry> | null | undefined) => {
  const data: Record<string, string> = {};
  const fieldLabels: Record<string, string> = {
    thirdPartyApps: '3rd Party Application',
    osPatches: 'OS Update',
  };

  if (!additionalInfo) return { data, fieldLabels };

  let thirdPartyCount = 0;
  let osUpdateCount = 0;

  Object.entries(additionalInfo).forEach(([key, entry]) => {
    if (!entry) return;
    if (isOsUpdateEntry(key, entry)) {
      osUpdateCount += 1;
    } else {
      thirdPartyCount += 1;
    }
  });

  if (thirdPartyCount > 0) data.thirdPartyApps = String(thirdPartyCount);
  if (osUpdateCount > 0) data.osPatches = String(osUpdateCount);

  return { data, fieldLabels };
};

const PachBySeverity = ({ patchBySeverity = [], groupId = '', severityOrder = [], durationOrder = [] }: PatchBySeverityProps) => {
  // console.log('patchBySeverity:', groupId);

  const navigate = useNavigate();
  const categories = severityOrder.length ? severityOrder : defaultCategories;
  const durations = durationOrder.length ? durationOrder : defaultDurations;

  const handleGridClick = async (_severity: string, _duration: string, urlFilter: string) => {
    navigate(`${urlFilter}&groupId=${groupId}`);
    // const tenantID = auth?.userData?.profile?.tenant_id as string;

    // const payload = {
    //   severity: severity.toLowerCase(),
    //   dayRange: mapDurationToRangeValue(duration),
    //   tenantGroupID: groupId,
    // };

    // try {
    //   setLoading(true);
    //   const response = await getSeverityRedirection(tenantID, payload);
    //   console.log('✅ Severity Redirection API Response:', response);

    //   // Optional: navigate or open a modal here
    //   // navigate(`/severity-details`, { state: { response } });
    // } catch (error) {
    //   console.error('❌ Error during severity redirection:', error);
    // } finally {
    //   setLoading(false);
    // }
  };

  return (
    <Card className={Styles['patch']}>
      <CardTitle title="Severity Heatmap" />

      <div className="grid grid-cols-5 gap-4 mt-5">
        <div />
        {categories.map((category) => (
          <HeadItem key={category}>{category}</HeadItem>
        ))}

        {durations.map((duration, rowIndex) => (
          <React.Fragment key={duration}>
            <HeadItem>{formatRangeLabel(duration)}</HeadItem>
            {categories.map((category) => {
              const normalizedDuration = normalizeRangeToken(duration);
              const match = patchBySeverity.find(
                (item) => item.category === category && normalizeRangeToken(item.duration) === normalizedDuration,
              );

              const count = getDisplayCount(match);
              const urlFilter = match?.urlFilter ?? '';
              const opacityClass = opacityMap[Math.min(rowIndex, opacityMap.length - 1)];
              const colorClass = colorMap[category] ?? 'bg-[#6C6C6C]';

              const { data: tooltipData, fieldLabels } = buildAdditionalInfoTooltip(match?.additionalInfo);
              const hasTooltipData = count > 0 && Object.keys(tooltipData).length > 0;

              return (
                <GridItem
                  key={`${category}-${duration}`}
                  className={`${colorClass} ${opacityClass}`}
                  onClick={() => handleGridClick(category, duration, urlFilter)}
                >
                  {hasTooltipData ? (
                    <DynamicDataTooltip data={tooltipData} fieldLabels={fieldLabels}>
                      <span>{count}</span>
                    </DynamicDataTooltip>
                  ) : (
                    count
                  )}
                </GridItem>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </Card>
  );
};

export default PachBySeverity;
